// Task actions. Each one applies the change locally at once (same rules as the server, see
// server/src/sync/mutations.ts) and queues the mutation; the server stays the source of truth.

import { planningDate, timeToMinutes } from "@shared/time";
import { aiEnabled } from "./ai";
import { uuidv7 } from "@shared/uuid";
import { db, getMeta, type PlanEntry, type Task, type TaskStatus } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();

async function me(): Promise<Me | undefined> {
  return getMeta<Me>("me");
}

function localRow<T extends object>(fields: T, spaceId: string) {
  const at = now();
  return { ...fields, space_id: spaceId, version: 0, deleted_at: null, updated_at: at, created_at: at };
}

async function activeEntry(taskId: string): Promise<PlanEntry | undefined> {
  return db.plan_entries
    .where("task_id")
    .equals(taskId)
    .filter((e) => e.status === "planned" && !e.deleted_at)
    .first();
}

/** Snel vastleggen: a new task in the inbox. Returns its id. */
export async function captureTask(
  title: string,
  options: { notes?: string | null; source?: "app" | "share"; categoryId?: string | null } = {},
): Promise<string> {
  const id = uuidv7();
  const at = now();
  const user = await me();
  const spaceId = user?.settings?.personal_space_id ?? "";
  // Read by the AI once it arrives (AI golf A); also what is captured offline (AI5).
  const ai = await aiEnabled();
  await sync.mutate(
    "task.create",
    {
      id,
      title: title.trim(),
      notes: options.notes ?? null,
      source: options.source ?? "app",
      categoryId: options.categoryId ?? null,
      ai,
      at,
    },
    [id],
    async () => {
      await db.tasks.add(
        localRow(
          {
            id,
            user_id: user?.user.id ?? "",
            title: title.trim(),
            notes: options.notes ?? null,
            status: "inbox" as TaskStatus,
            due_on: null,
            estimate_minutes: null,
            completed_at: null,
            inbox_at: at,
            source: options.source ?? "app",
            position: 0,
            category_id: options.categoryId ?? null,
            ai_state: ai ? "pending" : null,
          },
          spaceId,
        ) as Task,
      );
    },
  );
  return id;
}

export async function updateTask(
  id: string,
  patch: Partial<Pick<Task, "title" | "notes" | "due_on" | "estimate_minutes" | "category_id" | "remind_at">>,
): Promise<void> {
  await sync.mutate("task.update", { id, ...patch }, [id], async () => {
    await db.tasks.update(id, patch);
    if (patch.title !== undefined) {
      const active = await activeEntry(id);
      if (active) await db.plan_entries.update(active.id, { title: patch.title });
    }
  });
}

/** Inbox, Lijst (open), Ooit (someday), Weg (cancelled) or done; takes the task off its day if planned. */
export async function setTaskStatus(id: string, status: TaskStatus): Promise<void> {
  const at = now();
  const task = await db.tasks.get(id);
  const active = await activeEntry(id);
  // "Toch niet klaar": the check on today or a later day goes off too (same rule as the server).
  const from = await today();
  const reopen =
    !active && task?.status === "done" && status === "open"
      ? (
          await db.plan_entries
            .where("task_id")
            .equals(id)
            .filter((e) => e.status === "done" && !e.deleted_at && e.plan_date >= from)
            .sortBy("plan_date")
        ).pop()
      : undefined;
  const rows = [id, ...(active ? [active.id] : []), ...(reopen ? [reopen.id] : [])];
  await sync.mutate("task.setStatus", { id, status, at }, rows, async () => {
    await db.tasks.update(id, { status, completed_at: status === "done" ? at : null, ...(status === "inbox" ? { inbox_at: at } : {}) });
    if (active) await db.plan_entries.update(active.id, status === "done" ? { status: "done", completed_at: at } : { status: "unplanned" });
    if (reopen) await db.plan_entries.update(reopen.id, { status: "planned", completed_at: null });
  });
}

export interface PlanTarget {
  date: string;
  /** A block of the stored day, or of the day type when the day is not on this device yet. */
  dayBlockId?: string | null;
  templateBlockId?: string | null;
}

/** The same checks as the server does first, so a refusal is rare (review B1). */
async function today(): Promise<string> {
  const user = await me();
  return planningDate(new Date(), timeToMinutes(user?.settings?.day_starts_at ?? "04:00"));
}

export type PlanReason = "planned" | "nog_open_today" | "nog_open_tomorrow";

/** Returns false when it cannot be planned there (a day in the past, or the task is done). */
export async function planTask(taskId: string, target: PlanTarget, reason: PlanReason = "planned"): Promise<boolean> {
  const task = await db.tasks.get(taskId);
  if (!task || task.status === "done" || target.date < (await today())) return false;
  const entryId = uuidv7();
  const active = await activeEntry(taskId);
  const plan = await db.day_plans
    .where("plan_date")
    .equals(target.date)
    .filter((p) => !p.deleted_at)
    .first();
  let dayBlockId = target.dayBlockId ?? null;
  if (!dayBlockId && target.templateBlockId && plan) {
    const block = await db.day_blocks
      .where("day_plan_id")
      .equals(plan.id)
      .filter((b) => b.template_block_id === target.templateBlockId)
      .first();
    dayBlockId = block?.id ?? null;
  }
  const args = {
    taskId,
    entryId,
    date: target.date,
    dayBlockId,
    templateBlockId: dayBlockId ? null : (target.templateBlockId ?? null),
    reason,
  };
  await sync.mutate("task.plan", args, [taskId, entryId, ...(active ? [active.id] : [])], async () => {
    if (active) await db.plan_entries.update(active.id, { status: "moved" });
    await db.plan_entries.add(
      localRow(
        {
          id: entryId,
          user_id: task.user_id,
          plan_date: target.date,
          day_plan_id: plan?.id ?? "",
          day_block_id: dayBlockId,
          pending_template_block_id: dayBlockId ? null : (target.templateBlockId ?? null),
          kind: "task" as const,
          task_id: taskId,
          title: task.title,
          status: "planned" as const,
          completed_at: null,
          carried_from_id: active?.id ?? null,
          position: 0,
        },
        task.space_id,
      ) as PlanEntry,
    );
    if (task.status !== "open") await db.tasks.update(taskId, { status: "open", completed_at: null });
  });
  return true;
}

export async function setEntryDone(entry: PlanEntry, done: boolean): Promise<void> {
  if (!entry.task_id) return;
  // Undoing a check while the task is already planned elsewhere is refused by the server; skip it here too.
  if (!done && (await activeEntry(entry.task_id))) return;
  const at = now();
  const taskId = entry.task_id;
  await sync.mutate("planEntry.setDone", { id: entry.id, done, at }, [entry.id, taskId], async () => {
    await db.plan_entries.update(entry.id, done ? { status: "done", completed_at: at } : { status: "planned", completed_at: null });
    await db.tasks.update(taskId, done ? { status: "done", completed_at: at } : { status: "open", completed_at: null });
  });
}

/** Off their day and back to the list (Alles naar de lijst, Opnieuw beginnen, Welkom terug). */
export type UnplanReason = "back_to_list" | "all_to_list" | "start_over" | "welcome_back";

export async function unplanEntries(entries: PlanEntry[], reason: UnplanReason = "back_to_list"): Promise<void> {
  const planned = entries.filter((e) => e.status === "planned");
  if (!planned.length) return;
  const ids = planned.map((e) => e.id);
  const taskIds = planned.map((e) => e.task_id).filter((t): t is string => !!t);
  await sync.mutate("planEntries.unplan", { ids, reason }, [...ids, ...taskIds], async () => {
    await db.plan_entries.bulkUpdate(ids.map((key) => ({ key, changes: { status: "unplanned" as const } })));
    for (const t of taskIds) {
      const task = await db.tasks.get(t);
      if (task && task.status !== "done" && task.status !== "cancelled") await db.tasks.update(t, { status: "open" });
    }
  });
}

export async function restoreEntries(ids: string[]): Promise<void> {
  if (!ids.length) return;
  await sync.mutate("planEntries.restore", { ids }, ids, async () => {
    for (const id of ids) {
      const entry = await db.plan_entries.get(id);
      if (!entry?.task_id || entry.status !== "unplanned" || (await activeEntry(entry.task_id))) continue;
      const task = await db.tasks.get(entry.task_id);
      if (!task || task.status === "done" || task.status === "cancelled") continue;
      await db.plan_entries.update(id, { status: "planned" });
      await db.tasks.update(entry.task_id, { status: "open" });
    }
  });
}

export async function addStep(taskId: string, title: string, position: number): Promise<void> {
  const task = await db.tasks.get(taskId);
  if (!task) return;
  const id = uuidv7();
  await sync.mutate("step.add", { id, taskId, title: title.trim(), position }, [id], async () => {
    await db.task_steps.add(localRow({ id, task_id: taskId, title: title.trim(), position, checked_at: null }, task.space_id));
  });
}

export async function setStepChecked(id: string, checked: boolean): Promise<void> {
  const at = now();
  await sync.mutate("step.setChecked", { id, checked, at }, [id], async () => {
    await db.task_steps.update(id, { checked_at: checked ? at : null });
  });
}

export async function removeStep(id: string): Promise<void> {
  const at = now();
  await sync.mutate("step.remove", { id, at }, [id], async () => {
    await db.task_steps.update(id, { deleted_at: at });
  });
}

/** Out of Nieuw (golf 10, B51): kept in Te doen without a day, with a kind (category) or none (Overig). */
export async function keepTask(id: string, categoryId: string | null): Promise<void> {
  if (categoryId) await updateTask(id, { category_id: categoryId });
  await setTaskStatus(id, "open");
}
